import type { CategoryResponseDto } from '@repo/types';
import { COURSE_LEVEL_VALUES } from '@repo/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { COURSE_KIND } from '@theailab-dev/parchment';
import type { CourseKind } from '@theailab-dev/parchment';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createCourse, updateCourse, type UpdateCourseRequest } from '../../api/courseApi';
import ConfirmModal from '../../components/ui/ConfirmModal';
import InstructorSelectModal from '../../components/ui/InstructorSelectModal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useCategories } from '../../hooks/useCategories';
import { useCourseDetail } from '../../hooks/useCourseDetail';
import { formatDateTimeLocal } from '../../utils/dateTime';
import { getErrorMessage } from '../../utils/errorHandler';
import { getMaterialDisplayName } from '../../utils/materialUrl';

/**
 * CourseEdit 컴포넌트 - 강의 생성/편집 페이지
 *
 * 주요 흐름:
 * 1. URL에서 courseId 추출 (없으면 생성 모드)
 * 2. 편집 모드인 경우 기존 강의 정보 fetch
 * 3. 폼 입력값 관리 (useState)
 * 4. 저장 버튼 클릭 시 API 호출 (추후 구현)
 * 5. 저장 후 강의 상세 페이지로 이동
 */
const CourseEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const courseId = id ? parseInt(id, 10) : null;
  const isEditMode = courseId !== null;

  /**
   * 편집 모드인 경우 기존 강의 정보 fetch
   */
  const { data: course, isLoading } = useCourseDetail(courseId || 0, {
    enabled: isEditMode,
  });

  /**
   * 카테고리 목록 fetch
   * - 에러가 나도 폼 사용 가능하도록 처리
   */
  const { data: categoriesData } = useCategories();

  /**
   * 폼 상태 관리
   */
  const [formData, setFormData] = useState({
    title: '',
    kind: '' as CourseKind,
    level: '' as '' | '초급' | '중급' | '고급',
    isSpecial: false,
    parentCategoryId: '', // 대분류 (UI용)
    categoryId: '', // 소분류 (최종 카테고리 ID)
    instructorId: '',
    preknowledge: '',
    startAt: '',
    endAt: '',
    periodDays: '',
    originalPrice: '', // 원가 (필수) - product_item.original_price
  });

  /**
   * 로딩 및 에러 상태
   */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [showRemoveMaterialConfirm, setShowRemoveMaterialConfirm] = useState(false);
  const [materialFile, setMaterialFile] = useState<File | null>(null);
  const materialFileInputRef = useRef<HTMLInputElement>(null);

  const removeMaterialMutation = useMutation({
    mutationFn: async (cid: number) => {
      await updateCourse(cid, { materialsUrl: null });
    },
    onSuccess: (_, courseId) => {
      void queryClient.invalidateQueries({ queryKey: ['courses'] });
      void queryClient.invalidateQueries({ queryKey: ['course', courseId] });
    },
    onError: (err: unknown) => {
      setErrorModalMessage(getErrorMessage(err));
      setShowErrorModal(true);
    },
  });

  // Tiptap 에디터 (사전지식)
  const preknowledgeEditor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Image,
    ],
    content: formData.preknowledge,
    onUpdate: ({ editor }) => {
      setFormData((prev) => ({ ...prev, preknowledge: editor.getHTML() }));
    },
  });

  // 편집 모드인 경우 에디터 내용 업데이트
  useEffect(() => {
    if (preknowledgeEditor && course && isEditMode) {
      preknowledgeEditor.commands.setContent(course.preknowledge || '');
    }
  }, [preknowledgeEditor, course, isEditMode]);

  /**
   * 카테고리 데이터 정리
   * - 대분류: depth=1인 카테고리 (parentCategoryId가 null)
   * - 소분류: depth=2인 카테고리 (parentCategoryId가 대분류 ID)
   */
  const categories = useMemo(() => {
    const allCategories = categoriesData?.data || [];
    const parentCategories = allCategories.filter((cat: CategoryResponseDto) => cat.depth === 1);
    return { all: allCategories, parents: parentCategories };
  }, [categoriesData]);

  /**
   * 선택된 대분류의 소분류 목록
   * parentCategoryId가 선택된 대분류 ID와 일치하는 카테고리들
   */
  const childCategories = useMemo(() => {
    if (!formData.parentCategoryId) return [];
    const parentId = parseInt(formData.parentCategoryId, 10);
    return categories.all.filter((cat: CategoryResponseDto) => cat.parentCategoryId === parentId);
  }, [formData.parentCategoryId, categories.all]);

  /**
   * 강사 선택 모달 상태
   */
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [selectedInstructorName, setSelectedInstructorName] = useState<string>('');
  const isPeriodDaysDisabled = formData.endAt.trim() !== '';

  /**
   * 편집 모드인 경우 기존 데이터로 폼 초기화
   */
  useEffect(() => {
    if (course && isEditMode && categoriesData) {
      // 현재 카테고리 찾기
      const currentCategory = categoriesData.data.find((cat: CategoryResponseDto) => cat.id === course.category?.id);

      // 부모 카테고리 ID (소분류인 경우 parentCategoryId, 대분류인 경우 자기 자신)
      const parentId = currentCategory?.parentCategoryId
        ? currentCategory.parentCategoryId.toString()
        : currentCategory?.id?.toString() || '';

      setFormData({
        title: course.title || '',
        kind: course.kind,
        level: (course.level as '' | '초급' | '중급' | '고급') || '',
        isSpecial: course.isSpecial ?? false,
        parentCategoryId: parentId,
        categoryId: course.category?.id?.toString() || '',
        instructorId: course.instructor?.id?.toString() || '',
        preknowledge: course.preknowledge || '',
        startAt: formatDateTimeLocal(course.startAt),
        endAt: formatDateTimeLocal(course.endAt),
        periodDays: course.periodDays !== null && course.periodDays !== undefined ? course.periodDays.toString() : '',
        originalPrice: (course as any).price?.toString() || '',
      });

      // 강사 이름 설정
      if (course.instructor?.name) {
        setSelectedInstructorName(course.instructor.name);
      }
    }
  }, [course, isEditMode, categoriesData]);

  /**
   * 입력 필드 변경 핸들러
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // 대분류 변경 시 소분류 초기화
    if (name === 'parentCategoryId') {
      setFormData((prev) => ({ ...prev, [name]: value, categoryId: '' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  /**
   * 유료 특강 여부 변경 핸들러
   */
  const handleIsSpecialChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, isSpecial: e.target.value === 'true' }));
  };

  /**
   * 강사 선택 핸들러
   */
  const handleSelectInstructor = (instructorId: number, instructorName: string) => {
    setFormData((prev) => ({ ...prev, instructorId: instructorId.toString() }));
    setSelectedInstructorName(instructorName);
  };

  /**
   * 저장 버튼 핸들러
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 필수 필드 검증
    if (!formData.kind) {
      setErrorModalMessage('유형을 선택해주세요.');
      setShowErrorModal(true);
      return;
    }
    if (!formData.parentCategoryId) {
      setErrorModalMessage('카테고리(대분류)를 선택해주세요.');
      setShowErrorModal(true);
      return;
    }
    if (!formData.title.trim()) {
      setErrorModalMessage('제목을 입력해주세요.');
      setShowErrorModal(true);
      return;
    }
    if (!formData.instructorId) {
      setErrorModalMessage('강사를 선택해주세요.');
      setShowErrorModal(true);
      return;
    }

    // 가격 정보 검증 (필수)
    if (!formData.originalPrice.trim()) {
      setErrorModalMessage('원가를 입력해주세요.');
      setShowErrorModal(true);
      return;
    }
    const originalPrice = parseFloat(formData.originalPrice);
    if (isNaN(originalPrice) || originalPrice < 0) {
      setErrorModalMessage('원가는 0 이상의 숫자로 입력해주세요.');
      setShowErrorModal(true);
      return;
    }

    // 수강권 유효 기간 (일) 검증 (빈 값은 null로 취급)
    const trimmedPeriodDays = formData.periodDays.trim();
    if (trimmedPeriodDays) {
      const parsedPeriodDays = parseInt(trimmedPeriodDays, 10);
      if (isNaN(parsedPeriodDays) || parsedPeriodDays < 1) {
        setErrorModalMessage('수강권 유효 기간(일)은 1 이상의 숫자로 입력해주세요.');
        setShowErrorModal(true);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // 최종 카테고리 ID 결정: 소분류가 있으면 소분류, 없으면 대분류
      const finalCategoryId = formData.categoryId
        ? parseInt(formData.categoryId, 10)
        : formData.parentCategoryId
          ? parseInt(formData.parentCategoryId, 10)
          : null;

      if (!finalCategoryId) {
        setErrorModalMessage('카테고리를 선택해주세요.');
        setShowErrorModal(true);
        setIsSubmitting(false);
        return;
      }

      if (isEditMode && courseId && course) {
        // 수정 모드: 변경된 필드만 전송
        const updateData: UpdateCourseRequest = {};

        // 필수 필드 비교 (값이 없어도 원래 값과 다르면 변경사항으로 처리)
        const formKind = formData.kind;
        if (formKind !== course.kind) {
          updateData.kind = formKind;
        }

        const courseCategoryId = course.category?.id;
        if (finalCategoryId !== courseCategoryId) {
          updateData.categoryId = finalCategoryId;
        }

        const trimmedTitle = formData.title.trim();
        if (trimmedTitle !== (course.title || '')) {
          updateData.title = trimmedTitle;
        }

        const formInstructorId = parseInt(formData.instructorId, 10);
        const courseInstructorId = course.instructor?.id;
        if (formInstructorId !== courseInstructorId) {
          updateData.instructorId = formInstructorId;
        }

        if (formInstructorId !== courseInstructorId) {
          updateData.instructorId = formInstructorId;
        }

        const formLevel = (formData.level || null) as '초급' | '중급' | '고급' | null;
        const courseLevel = (course.level || null) as '초급' | '중급' | '고급' | null;
        if (formLevel !== courseLevel) {
          updateData.level = formLevel;
        }

        const formPreknowledge = formData.preknowledge || null;
        const coursePreknowledge = course.preknowledge || null;
        if (formPreknowledge !== coursePreknowledge) {
          updateData.preknowledge = formPreknowledge;
        }

        // 날짜 비교 (ISO 문자열로 변환하여 비교, 시간대 고려)
        const formStartAtISO = formData.startAt ? new Date(formData.startAt).toISOString() : null;
        const courseStartAtISO = course.startAt ? new Date(course.startAt).toISOString() : null;
        if (formStartAtISO !== courseStartAtISO) {
          updateData.startAt = formStartAtISO;
        }

        const formEndAtISO = formData.endAt ? new Date(formData.endAt).toISOString() : null;
        const courseEndAtISO = course.endAt ? new Date(course.endAt).toISOString() : null;
        if (formEndAtISO !== courseEndAtISO) {
          updateData.endAt = formEndAtISO;
        }

        // 종료 일시가 있으면 periodDays는 UX 상 비활성화하며 전송하지 않음
        if (!isPeriodDaysDisabled) {
          const formPeriodDays = formData.periodDays.trim() === '' ? null : parseInt(formData.periodDays, 10);
          const coursePeriodDays = course.periodDays ?? null;
          if (formPeriodDays !== coursePeriodDays) {
            updateData.periodDays = formPeriodDays;
          }
        }

        // 유료 특강 여부 비교
        if (formData.isSpecial !== (course.isSpecial ?? false)) {
          updateData.isSpecial = formData.isSpecial;
        }

        // 원가 비교
        const formOriginalPrice = parseInt(formData.originalPrice, 10);
        const courseOriginalPrice = (course as any).price;
        if (formOriginalPrice !== courseOriginalPrice) {
          // @ts-ignore: originalPrice might not be in UpdateCourseRequest type yet
          (updateData as any).originalPrice = formOriginalPrice;
        }

        // 변경사항이 있거나 강의 자료 파일이 있는 경우에만 API 호출
        const hasChanges = Object.keys(updateData).length > 0 || materialFile !== null;
        if (hasChanges) {
          await updateCourse(courseId, updateData, materialFile ?? undefined);

          // 강의 목록 및 상세 캐시 무효화
          await queryClient.invalidateQueries({ queryKey: ['courses'] });
          await queryClient.invalidateQueries({ queryKey: ['course', courseId] });
        }

        navigate(`/dashboard/courses/${courseId}`);
      } else {
        // 생성 모드: 모든 필드 전송
        const requestData = {
          kind: formData.kind,
          categoryId: finalCategoryId,
          title: formData.title.trim(),
          instructorId: parseInt(formData.instructorId, 10),
          originalPrice: parseInt(formData.originalPrice, 10),
          isSpecial: formData.isSpecial,
          // 선택 필드 (값이 있을 때만)
          ...(formData.level && {
            level: formData.level as '초급' | '중급' | '고급',
          }),
          ...(formData.preknowledge && {
            preknowledge: formData.preknowledge,
          }),
          ...(formData.startAt && {
            startAt: new Date(formData.startAt),
          }),
          ...(formData.endAt && {
            endAt: new Date(formData.endAt),
          }),
          ...(!isPeriodDaysDisabled &&
            formData.periodDays.trim() !== '' && {
              periodDays: parseInt(formData.periodDays, 10),
            }),
        };

        // 생성 모드: 히스토리를 교체하여 뒤로가기 시 생성 화면으로 돌아가지 않도록 함
        // @ts-ignore: originalPrice is not in CreateCourseDto yet
        const newCourse = await createCourse(requestData);

        await queryClient.invalidateQueries({ queryKey: ['courses'] });

        navigate(`/dashboard/courses/${newCourse.id}`, { replace: true });
      }
    } catch (error: unknown) {
      console.error('❌ 강의 저장 실패:', error);

      // 서버에서 반환된 에러는 팝업으로 표시
      // (프론트엔드에서 버튼을 막았지만, 다른 경로로 요청이 갈 수 있으므로)
      const errorMessage = getErrorMessage(error);
      setErrorModalMessage(errorMessage);
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 취소 버튼 핸들러
   * - 편집 모드: navigate(-1)로 이전 페이지(상세 화면)로 돌아가기
   * - 생성 모드: 목록으로 이동 (replace로 히스토리 교체)
   */
  const handleCancel = () => {
    if (isEditMode) {
      // 편집 모드: 브라우저 히스토리 기반 뒤로가기
      navigate(-1);
    } else {
      // 생성 모드: 목록으로 이동 (히스토리 교체)
      navigate('/dashboard/courses', { replace: true });
    }
  };

  const handleRemoveMaterial = () => {
    if (!courseId) return;
    setShowRemoveMaterialConfirm(false);
    removeMaterialMutation.mutate(courseId);
  };

  // 로딩 상태
  if (isEditMode && isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-slate-100 p-8">
      <div className="mx-auto max-w-4xl">
        {/* 헤더 */}
        <div className="mb-8">
          <h2 className="m-0 text-3xl font-bold text-slate-800">{isEditMode ? '강의 편집' : '새 강의 만들기'}</h2>
          <p className="mt-2 text-slate-600">
            {isEditMode ? '강의의 기본 정보를 수정합니다.' : '새로운 강의를 생성합니다.'}
          </p>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 기본 정보 섹션 */}
          <div className="rounded-xl bg-white p-6 shadow-sm">
            <h3 className="mb-6 text-xl font-semibold text-slate-800">기본 정보</h3>

            <div className="space-y-4">
              {/* 제목 */}
              <div>
                <label htmlFor="title" className="mb-2 block text-sm font-medium text-slate-700">
                  제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  placeholder="강의 제목을 입력하세요"
                />
              </div>

              {/* 종류 & 레벨 & 유료 특강 여부 & 원가 */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {/* 종류 */}
                <div>
                  <label htmlFor="kind" className="mb-2 block text-sm font-medium text-slate-700">
                    종류 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="kind"
                    name="kind"
                    value={formData.kind}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                    <option value="">선택하세요</option>
                    {Object.values(COURSE_KIND).map((kind) => (
                      <option key={kind} value={kind}>
                        {kind}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 레벨 */}
                <div>
                  <label htmlFor="level" className="mb-2 block text-sm font-medium text-slate-700">
                    난이도
                  </label>
                  <select
                    id="level"
                    name="level"
                    value={formData.level}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                    <option value="">선택하세요</option>
                    {COURSE_LEVEL_VALUES.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 유료 특강 여부 */}
                <div>
                  <label htmlFor="isSpecial" className="mb-2 block text-sm font-medium text-slate-700">
                    유료 특강 여부
                  </label>
                  <select
                    id="isSpecial"
                    name="isSpecial"
                    value={formData.isSpecial ? 'true' : 'false'}
                    onChange={handleIsSpecialChange}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                    <option value="false">일반 강의</option>
                    <option value="true">유료 특강</option>
                  </select>
                </div>

                {/* 원가 */}
                <div>
                  <label htmlFor="originalPrice" className="mb-2 block text-sm font-medium text-slate-700">
                    원가 (원) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="originalPrice"
                    name="originalPrice"
                    value={formData.originalPrice}
                    onChange={handleChange}
                    required
                    min="0"
                    step="1000"
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    placeholder="100000"
                  />
                </div>
              </div>

              {/* 사전 지식 */}
              <div>
                <label htmlFor="preknowledge" className="mb-2 block text-sm font-medium text-slate-700">
                  사전 지식
                </label>
                <div className="overflow-hidden rounded-lg border border-slate-300">
                  {/* 툴바 */}
                  {preknowledgeEditor && (
                    <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50 p-2">
                      <button
                        type="button"
                        onClick={() => preknowledgeEditor.chain().focus().toggleBold().run()}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          preknowledgeEditor.isActive('bold') ? 'bg-slate-300' : ''
                        }`}>
                        <strong>B</strong>
                      </button>
                      <button
                        type="button"
                        onClick={() => preknowledgeEditor.chain().focus().toggleItalic().run()}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          preknowledgeEditor.isActive('italic') ? 'bg-slate-300' : ''
                        }`}>
                        <em>I</em>
                      </button>
                      <button
                        type="button"
                        onClick={() => preknowledgeEditor.chain().focus().toggleUnderline().run()}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          preknowledgeEditor.isActive('underline') ? 'bg-slate-300' : ''
                        }`}>
                        <u>U</u>
                      </button>
                      <button
                        type="button"
                        onClick={() => preknowledgeEditor.chain().focus().toggleStrike().run()}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          preknowledgeEditor.isActive('strike') ? 'bg-slate-300' : ''
                        }`}>
                        <s>S</s>
                      </button>
                      <div className="mx-1 h-6 w-px bg-slate-300" />
                      <button
                        type="button"
                        onClick={() => preknowledgeEditor.chain().focus().toggleHeading({ level: 1 }).run()}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          preknowledgeEditor.isActive('heading', { level: 1 }) ? 'bg-slate-300' : ''
                        }`}>
                        H1
                      </button>
                      <button
                        type="button"
                        onClick={() => preknowledgeEditor.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          preknowledgeEditor.isActive('heading', { level: 2 }) ? 'bg-slate-300' : ''
                        }`}>
                        H2
                      </button>
                      <button
                        type="button"
                        onClick={() => preknowledgeEditor.chain().focus().toggleHeading({ level: 3 }).run()}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          preknowledgeEditor.isActive('heading', { level: 3 }) ? 'bg-slate-300' : ''
                        }`}>
                        H3
                      </button>
                      <div className="mx-1 h-6 w-px bg-slate-300" />
                      <button
                        type="button"
                        onClick={() => preknowledgeEditor.chain().focus().toggleBulletList().run()}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          preknowledgeEditor.isActive('bulletList') ? 'bg-slate-300' : ''
                        }`}>
                        •
                      </button>
                      <button
                        type="button"
                        onClick={() => preknowledgeEditor.chain().focus().toggleOrderedList().run()}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          preknowledgeEditor.isActive('orderedList') ? 'bg-slate-300' : ''
                        }`}>
                        1.
                      </button>
                      <div className="mx-1 h-6 w-px bg-slate-300" />
                      <button
                        type="button"
                        onClick={() => {
                          const url = window.prompt('링크 URL을 입력하세요:');
                          if (url) {
                            preknowledgeEditor.chain().focus().setLink({ href: url }).run();
                          }
                        }}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          preknowledgeEditor.isActive('link') ? 'bg-slate-300' : ''
                        }`}>
                        링크
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const url = window.prompt('이미지 URL을 입력하세요:');
                          if (url) {
                            preknowledgeEditor.chain().focus().setImage({ src: url }).run();
                          }
                        }}
                        className="rounded px-2 py-1 text-sm hover:bg-slate-200">
                        이미지
                      </button>
                      <div className="mx-1 h-6 w-px bg-slate-300" />
                      <button
                        type="button"
                        onClick={() => setShowHtmlSource(!showHtmlSource)}
                        className={`rounded px-2 py-1 text-sm hover:bg-slate-200 ${
                          showHtmlSource ? 'bg-slate-300' : ''
                        }`}>
                        HTML
                      </button>
                    </div>
                  )}
                  {/* 에디터 또는 HTML 소스 */}
                  {showHtmlSource ? (
                    <textarea
                      value={preknowledgeEditor?.getHTML() || ''}
                      onChange={(e) => {
                        if (preknowledgeEditor) {
                          preknowledgeEditor.commands.setContent(e.target.value);
                        }
                      }}
                      className="min-h-[150px] w-full resize-y border-none p-4 font-mono text-sm outline-none"
                      placeholder="HTML 코드를 입력하세요..."
                    />
                  ) : (
                    <div className="[&_.ProseMirror]:border-none [&_.ProseMirror]:outline-none [&_.ProseMirror:focus]:border-none [&_.ProseMirror:focus]:outline-none [&_.ProseMirror:focus-visible]:border-none [&_.ProseMirror:focus-visible]:outline-none">
                      <EditorContent
                        editor={preknowledgeEditor}
                        className="prose prose-sm min-h-[150px] max-w-none p-4"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 강의 자료 (편집 모드) */}
          {isEditMode && courseId && course && (
            <div className="rounded-xl bg-white p-6 shadow-sm">
              <h3 className="mb-6 text-xl font-semibold text-slate-800">강의 자료</h3>
              <div className="space-y-4">
                {(course as { materialsUrl?: string | null }).materialsUrl ? (
                  <>
                    <div className="flex flex-wrap items-center gap-4">
                      <a
                        href={(course as { materialsUrl: string }).materialsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="max-w-md truncate text-indigo-600 hover:underline"
                        title={(course as { materialsUrl: string }).materialsUrl}>
                        {getMaterialDisplayName((course as { materialsUrl: string }).materialsUrl)}
                      </a>
                      <button
                        type="button"
                        onClick={() => setShowRemoveMaterialConfirm(true)}
                        disabled={removeMaterialMutation.isPending}
                        className="rounded-lg bg-slate-200 px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-300 disabled:opacity-50">
                        {removeMaterialMutation.isPending ? '처리 중...' : '강의 자료 제거'}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">
                      단일 파일 등록 가능. 새 파일을 선택하면 저장 시 기존 자료를 대체합니다.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-600">
                    등록된 강의 자료가 없습니다. 아래에서 파일을 선택한 뒤 저장하면 등록됩니다.
                  </p>
                )}

                {/* 파일 선택 (등록 또는 변경) */}
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    ref={materialFileInputRef}
                    type="file"
                    name="materialFile"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.zip,.hwp,image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setMaterialFile(file ?? null);
                    }}
                    className="hidden"
                  />
                  {materialFile ? (
                    <>
                      <span className="max-w-xs truncate text-sm text-slate-700">{materialFile.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setMaterialFile(null);
                          if (materialFileInputRef.current) materialFileInputRef.current.value = '';
                        }}
                        className="rounded-lg bg-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-300">
                        선택 취소
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => materialFileInputRef.current?.click()}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                      {(course as { materialsUrl?: string | null }).materialsUrl
                        ? '파일 선택 (변경)'
                        : '파일 선택 (등록)'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 분류 섹션 */}
          <div className="rounded-xl bg-white p-6 shadow-sm">
            <h3 className="mb-6 text-xl font-semibold text-slate-800">분류</h3>

            <div className="space-y-4">
              {/* 카테고리 - 2단계 */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* 대분류 */}
                <div>
                  <label htmlFor="parentCategoryId" className="mb-2 block text-sm font-medium text-slate-700">
                    카테고리 (대분류) <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="parentCategoryId"
                    name="parentCategoryId"
                    value={formData.parentCategoryId}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                    <option value="">선택하세요</option>
                    {categories.parents.length === 0 && (
                      <option value="" disabled>
                        카테고리 데이터 없음 (백엔드 API 구현 필요)
                      </option>
                    )}
                    {categories.parents.map((category: CategoryResponseDto) => (
                      <option key={category.id} value={category.id}>
                        {category.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 소분류 */}
                <div>
                  <label htmlFor="categoryId" className="mb-2 block text-sm font-medium text-slate-700">
                    카테고리 (소분류)
                  </label>
                  <select
                    id="categoryId"
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleChange}
                    disabled={!formData.parentCategoryId}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100">
                    <option value="">선택하세요</option>
                    {childCategories.map((category: CategoryResponseDto) => (
                      <option key={category.id} value={category.id}>
                        {category.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 강사 */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  강사 <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowInstructorModal(true)}
                  className={`w-full rounded-lg border border-slate-300 px-4 py-2.5 text-left focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none ${
                    formData.instructorId ? 'bg-white text-slate-800' : 'bg-white text-slate-400'
                  }`}>
                  {selectedInstructorName || formData.instructorId
                    ? selectedInstructorName || '강사 선택됨'
                    : '강사 선택하기'}
                </button>
                {formData.instructorId && (
                  <p className="mt-2 text-sm text-slate-500">선택된 강사 ID: {formData.instructorId}</p>
                )}
              </div>
            </div>
          </div>

          {/* 기간 섹션 */}
          <div className="rounded-xl bg-white p-6 shadow-sm">
            <h3 className="mb-6 text-xl font-semibold text-slate-800">강의 기간</h3>

            <div className="space-y-4">
              {/* 시작 일시 */}
              <div>
                <label htmlFor="startAt" className="mb-2 block text-sm font-medium text-slate-700">
                  시작 일시
                </label>
                <input
                  type="datetime-local"
                  id="startAt"
                  name="startAt"
                  value={formData.startAt}
                  onChange={handleChange}
                  className="w-full max-w-md rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
                <p className="mt-1 text-xs text-slate-500">강의가 시작되는 날짜와 시각을 입력하세요</p>
              </div>

              {/* 종료 일시 */}
              <div>
                <label htmlFor="endAt" className="mb-2 block text-sm font-medium text-slate-700">
                  종료 일시
                </label>
                <input
                  type="datetime-local"
                  id="endAt"
                  name="endAt"
                  value={formData.endAt}
                  onChange={handleChange}
                  className="w-full max-w-md rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
                <p className="mt-1 text-xs text-slate-500">강의가 종료되는 날짜와 시각을 입력하세요</p>
              </div>

              {/* 수강권 유효 기간 */}
              <div>
                <label htmlFor="periodDays" className="mb-2 block text-sm font-medium text-slate-700">
                  수강권 유효 기간 (일)
                </label>
                <input
                  type="number"
                  id="periodDays"
                  name="periodDays"
                  value={formData.periodDays}
                  onChange={handleChange}
                  min={1}
                  disabled={isPeriodDaysDisabled}
                  placeholder="예: 30"
                  className="w-full max-w-md rounded-lg border border-slate-300 px-4 py-2.5 focus:border-transparent focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                />
                <p className="mt-1 text-xs text-slate-500">
                  수강권 만료일 계산 규칙: <br />
                  1) 종료 일시가 있으면 `수강권 유효 기간`은 무시됩니다. <br />
                  2) 종료 일시가 없고 시작 일시가 있으면 `시작 일시 + 수강권 유효 기간(일)`로 계산됩니다. <br />
                  3) 시작 일시도 없으면 현재 날짜 기준으로 `수강권 유효 기간`은 적용됩니다. <br />
                  (빈 값은 제한 없음) <br />
                </p>
                {isPeriodDaysDisabled && (
                  <p className="mt-1 text-xs text-amber-600">
                    종료 일시가 설정되어 있어 수강권 유효 기간 입력은 비활성화되었습니다.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* 버튼 영역 */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-lg bg-slate-200 px-6 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-300">
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-indigo-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
              {isSubmitting ? '처리 중...' : isEditMode ? '저장' : '생성'}
            </button>
          </div>
        </form>

        {/* 강사 선택 모달 */}
        <InstructorSelectModal
          isOpen={showInstructorModal}
          onClose={() => setShowInstructorModal(false)}
          onSelect={handleSelectInstructor}
          selectedInstructorId={formData.instructorId ? parseInt(formData.instructorId, 10) : null}
        />

        {/* 에러 모달 (서버에서 반환된 에러 처리) */}
        <ConfirmModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          type="error"
          title="저장 실패"
          message={errorModalMessage}
          buttons={[
            {
              label: '확인',
              onClick: () => setShowErrorModal(false),
              variant: 'primary',
            },
          ]}
        />

        {/* 강의 자료 제거 확인 모달 */}
        <ConfirmModal
          isOpen={showRemoveMaterialConfirm}
          onClose={() => setShowRemoveMaterialConfirm(false)}
          type="warning"
          title="강의 자료 제거"
          message="강의 자료 링크를 제거하시겠습니까?"
          buttons={[
            {
              label: '취소',
              onClick: () => setShowRemoveMaterialConfirm(false),
              variant: 'secondary',
            },
            {
              label: '제거',
              onClick: handleRemoveMaterial,
              variant: 'danger',
            },
          ]}
        />
      </div>
    </div>
  );
};

export default CourseEdit;
